import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { TradingInputKind } from '@/api/trading';
import type { StrategyDocumentDto } from '@/api/types';
import { normalizeDirectionValue } from '@/components/trading/OperatorInputsSection';
import styles from '@/pages/TradingPage.module.css';

const inputTypeIds: Record<string, TradingInputKind> = {
  'input.number': 'Number',
  'input.boolean': 'Boolean',
  'input.direction': 'Direction',
  'input.text': 'Text',
};

const directionOptions = ['LONG', 'OFF', 'SHORT'] as const;

export interface DeclaredOperatorInput {
  key: string;
  kind: TradingInputKind;
  label: string | null;
  defaultValue: unknown;
}

export function collectDeclaredOperatorInputs(document: StrategyDocumentDto | undefined): DeclaredOperatorInput[] {
  if (!document?.definition) return [];
  const nodes = [
    ...(document.definition.pipelines ?? []).flatMap((pipeline) => pipeline.nodes ?? []),
    ...(document.definition.components ?? []).flatMap((component) => component.nodes ?? []),
  ];
  const found = new Map<string, DeclaredOperatorInput>();
  for (const node of nodes) {
    if (node.kind !== 'Primitive') continue;
    const kind = inputTypeIds[node.typeId];
    if (!kind) continue;
    const key = typeof node.config?.key === 'string' ? node.config.key.trim() : '';
    if (!key || found.has(key)) continue;
    const rawLabel = node.config?.label;
    const label = typeof rawLabel === 'string' && rawLabel.trim() ? rawLabel : null;
    found.set(key, { key, kind, label, defaultValue: node.config?.defaultValue });
  }
  return [...found.values()];
}

function matchesDeclaredDefault(input: DeclaredOperatorInput, value: unknown) {
  if (value === null) return false;
  switch (input.kind) {
    case 'Number':
      return Number.isFinite(Number(input.defaultValue)) && Number(input.defaultValue) === Number(value);
    case 'Boolean':
      return input.defaultValue === value;
    case 'Direction':
      return normalizeDirectionValue(String(input.defaultValue ?? 'None')) === value;
    default:
      return String(input.defaultValue ?? '') === String(value);
  }
}

export function withOperatorInput(values: Record<string, unknown>, input: DeclaredOperatorInput, value: unknown | null): Record<string, unknown> {
  const next = { ...values };
  if (value === null || matchesDeclaredDefault(input, value)) delete next[input.key];
  else next[input.key] = value;
  return next;
}

export function buildOperatorInputs(values: Record<string, unknown>, inputs: readonly DeclaredOperatorInput[]): Record<string, unknown> | undefined {
  const declaredKeys = new Set(inputs.map((input) => input.key));
  const entries = Object.entries(values).filter(([key]) => declaredKeys.has(key));
  return entries.length ? Object.fromEntries(entries) : undefined;
}

/**
 * Keys a live operator must send to move the engine from `effective` to `draft`: changed values as
 * typed, keys dropped from the draft as null (revert to the declared default).
 */
export function diffOperatorInputChanges(
  draft: Record<string, unknown>,
  effective: Record<string, unknown>,
): Record<string, unknown | null> {
  const next: Record<string, unknown | null> = {};
  const keys = new Set([...Object.keys(draft), ...Object.keys(effective)]);
  for (const key of keys) {
    const inDraft = Object.prototype.hasOwnProperty.call(draft, key);
    const inEffective = Object.prototype.hasOwnProperty.call(effective, key);
    if (!inDraft) {
      if (inEffective) next[key] = null;
      continue;
    }

    if (!inEffective || !sameValue(draft[key], effective[key])) next[key] = draft[key];
  }

  return next;
}

function sameValue(left: unknown, right: unknown) {
  if (left === right) return true;
  if (typeof left === 'number' || typeof right === 'number') return Number(left) === Number(right);
  if (typeof left === 'boolean' || typeof right === 'boolean') return left === right;
  return String(left) === String(right);
}

function defaultText(input: DeclaredOperatorInput) {
  if (input.kind === 'Direction') return normalizeDirectionValue(String(input.defaultValue ?? 'None')).toLowerCase();
  if (input.kind === 'Number') return Number.isFinite(Number(input.defaultValue)) ? String(Number(input.defaultValue)) : '';
  if (input.kind === 'Boolean') return input.defaultValue === true ? 'true' : 'false';
  return typeof input.defaultValue === 'string' ? input.defaultValue : '';
}

interface BacktestOperatorInputsProps {
  inputs: readonly DeclaredOperatorInput[];
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Key → UTC time the queued change becomes effective; renders a badge under the control. */
  pendingBy?: Record<string, string>;
  legend?: string;
}

export function BacktestOperatorInputs({ inputs, values, onChange, pendingBy, legend }: BacktestOperatorInputsProps) {
  if (!inputs.length) return null;
  return <fieldset className={styles.fieldset}>
    <legend>{legend ?? 'Operator inputs'}</legend>
    {inputs.map((input) => {
      const changed = Object.prototype.hasOwnProperty.call(values, input.key);
      const control = input.kind === 'Direction'
        ? <span className={styles.actions}>
          {directionOptions.map((option) => <Button
            key={option}
            type="button"
            variant={(values[input.key] ?? normalizeDirectionValue(String(input.defaultValue ?? 'None'))) === option ? 'primary' : 'ghost'}
            aria-pressed={(values[input.key] ?? normalizeDirectionValue(String(input.defaultValue ?? 'None'))) === option}
            onClick={() => onChange(withOperatorInput(values, input, option))}
          >{option}</Button>)}
        </span>
        : input.kind === 'Number'
          ? <Input type="number" aria-label={input.label ?? input.key} value={changed ? String(values[input.key]) : defaultText(input)} onChange={(event) => {
            const text = event.target.value;
            if (text.trim() === '') onChange(withOperatorInput(values, input, null));
            else if (Number.isFinite(Number(text))) onChange(withOperatorInput(values, input, Number(text)));
          }}/>
          : input.kind === 'Boolean'
            ? <Input type="checkbox" aria-label={input.label ?? input.key} checked={(values[input.key] ?? input.defaultValue === true) === true} onChange={(event) => onChange(withOperatorInput(values, input, event.target.checked))}/>
            : <Input type="text" aria-label={input.label ?? input.key} value={changed ? String(values[input.key]) : defaultText(input)} onChange={(event) => onChange(withOperatorInput(values, input, event.target.value))}/>;
      return <label key={input.key}>
        <span><strong>{input.label ?? input.key}</strong><small className={styles.block}>{input.key} · default {defaultText(input)}</small>
          {pendingBy?.[input.key]
            ? <small className={styles.block}>pending → {pendingBy[input.key].slice(11, 19)} UTC</small>
            : null}
        </span>
        {control}
        {changed && <Button type="button" variant="ghost" onClick={() => onChange(withOperatorInput(values, input, null))}>Default</Button>}
      </label>;
    })}
  </fieldset>;
}
